import { u8 } from "./bytes.js";
import {
  BANKS,
  BIT_HAS_CHANGED_BOXES,
  BOX_DATA_LENGTH,
  BOXES_PER_BANK,
  F,
  S,
  SRAM_SIZE,
} from "./layout.js";

/** `CalcCheckSum` (engine/menus/save.asm): 8-bit sum, complemented. */
export function calcChecksum(b: Uint8Array, start: number, endExclusive: number): number {
  let sum = 0;
  for (let i = start; i < endExclusive; i++) sum = (sum + u8(b, i)) & 0xff;
  return ~sum & 0xff;
}

function assertSize(sram: Uint8Array): void {
  if (sram.length !== SRAM_SIZE) throw new RangeError(`SRAM must be ${SRAM_SIZE} bytes, got ${sram.length}`);
}

/** Main checksum over sGameData..sGameDataEnd-1 (0x2598..0x3522). */
export function mainChecksum(sram: Uint8Array): number {
  return calcChecksum(sram, S.sGameData, S.sGameDataEnd);
}

export function mainChecksumValid(sram: Uint8Array): boolean {
  assertSize(sram);
  return u8(sram, S.sMainDataCheckSum) === mainChecksum(sram);
}

function bankValid(sram: Uint8Array, bankIdx: 0 | 1): boolean {
  const bank = BANKS[bankIdx];
  const allEnd = bank.start + BOXES_PER_BANK * BOX_DATA_LENGTH;
  if (u8(sram, bank.allChecksum) !== calcChecksum(sram, bank.start, allEnd)) return false;
  for (let i = 0; i < BOXES_PER_BANK; i++) {
    const s = bank.start + i * BOX_DATA_LENGTH;
    if (u8(sram, bank.boxChecksums + i) !== calcChecksum(sram, s, s + BOX_DATA_LENGTH)) return false;
  }
  return true;
}

export function boxesInitialised(sram: Uint8Array): boolean {
  return ((u8(sram, F.currentBoxNum) >> BIT_HAS_CHANGED_BOXES) & 1) === 1;
}

/**
 * Validates the main checksum and both stored-box banks (all-boxes sum plus the six
 * per-box sums each). While BIT_HAS_CHANGED_BOXES is clear the game has never written
 * banks 2–3 (they hold power-on garbage and are never read), so they are reported valid.
 */
export function validateChecksums(sram: Uint8Array): { main: boolean; banks: [boolean, boolean] } {
  assertSize(sram);
  const main = mainChecksumValid(sram);
  if (!boxesInitialised(sram)) return { main, banks: [true, true] };
  return { main, banks: [bankValid(sram, 0), bankValid(sram, 1)] };
}

export function writeMainChecksum(sram: Uint8Array): void {
  assertSize(sram);
  sram[S.sMainDataCheckSum] = mainChecksum(sram);
}

/** Same order as CopyBoxToOrFromSRAM: all-boxes checksum, then CalcIndividualBoxCheckSums. */
export function writeBankChecksums(sram: Uint8Array, bankIdx: 0 | 1): void {
  assertSize(sram);
  const bank = BANKS[bankIdx];
  const allEnd = bank.start + BOXES_PER_BANK * BOX_DATA_LENGTH;
  sram[bank.allChecksum] = calcChecksum(sram, bank.start, allEnd);
  for (let i = 0; i < BOXES_PER_BANK; i++) {
    const s = bank.start + i * BOX_DATA_LENGTH;
    sram[bank.boxChecksums + i] = calcChecksum(sram, s, s + BOX_DATA_LENGTH);
  }
}

/** Main checksum plus both banks (all-boxes and per-box), as the composer requires (§13.1 step 5). */
export function writeChecksums(sram: Uint8Array): void {
  writeMainChecksum(sram);
  writeBankChecksums(sram, 0);
  writeBankChecksums(sram, 1);
}
